import { splitRoles, CLIENT_HOME_ROLES } from "./userRoles";

/** Aligns with legacy admin-only UI; server now uses `Roles.IsCustomerRole`. Kept for documentation. */
export const MEB_FEE_OVERRIDE_ROLE_NAMES = ["Super Admin", "MISC Filer", "Outsource Staff"] as const;

/** True if the user has any role that is not a client-portal home role (internal / MISC staff). */
export function hasNonClientHomeRole(userRoles?: string): boolean {
  const roles = splitRoles(userRoles);
  if (roles.length === 0) return false;
  const clientSet = CLIENT_HOME_ROLES as readonly string[];
  return roles.some((role) => !clientSet.includes(role));
}

/** Month End Billing fee override save: any internal (non–client-home) role; client-only users cannot save. */
export function userCanSaveMonthEndBillingFeeOverride(userRoles?: string): boolean {
  return hasNonClientHomeRole(userRoles);
}
/** Same segments as SQL `LineSignature` for MEB rows: Filed | DateFinal | NeedDiligentSearch (trimmed). Prefer API row fields so the key matches `getMonthEndBilling.sql`. */
export function buildMonthEndBillingLineSignature(row: {
  filed?: unknown;
  dateFinal?: unknown;
  needDiligentSearch?: unknown;
}): string {
  const f = (row.filed ?? "").toString().trim();
  const d = (row.dateFinal ?? "").toString().trim();
  const n = (row.needDiligentSearch ?? "").toString().trim();
  return `${f}|${d}|${n}`;
}

/**
 * Billable Filed Transactions row: `getMonthEndBilling.sql` leaves `NeedDiligentSearch` '' on
 * filing/NY-binder unions. The Diligent Search queue union sets TRUE/FALSE and bills
 * `DiligentSearchFee` (Ally $120, Intrepid $43, etc.). PDF uses `isDiligent: 1` (no queue union).
 */
export function isBillableFilingRowForMebUi(item: { needDiligentSearch?: unknown }): boolean {
  return (item.needDiligentSearch ?? "").toString().trim() === "";
}

export function billableFilingRowsForMebUi<T extends { needDiligentSearch?: unknown }>(rows: T[]): T[] {
  return rows.filter(isBillableFilingRowForMebUi);
}

/**
 * Filed Transactions Excel/CSV: same rows as the MEB grid and PDF. Selected rows already come
 * from the grid (billable-only). Comprehensive ZIP `customerPolicyIds` still uses full listData.
 */
export function rowsForFiledTransactionsExport<T extends { needDiligentSearch?: unknown }>(
  allRows: T[],
  selectedRows?: T[] | null
): T[] {
  if (selectedRows && selectedRows.length > 0) return selectedRows;
  return billableFilingRowsForMebUi(allRows);
}

/** Parse API/grid fee text (currency, commas, parens) to a finite number for totals / Excel. */
export function parseFeeDueToNumber(raw: unknown): number {
  const s = normalizeFeeDueOverrideForApi(String(raw ?? ""));
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

export function sumFeeDueForRows(rows: Array<{ feeDue?: unknown }>): number {
  return rows.reduce((sum, row) => sum + parseFeeDueToNumber(row.feeDue), 0);
}

/** Strip currency formatting for `FeeDueOverride` VARCHAR persisted value. */
export function normalizeFeeDueOverrideForApi(raw: string): string {
  let s = (raw ?? "").toString().trim().replace(/\$/g, "").replace(/,/g, "");
  const parenNeg = s.match(/^\(([^)]+)\)$/);
  if (parenNeg) s = `-${parenNeg[1]}`;
  s = s.replace(/[()]/g, "");
  const t = s.trim();
  return t.length ? t : "0";
}
